import type { DataTableVisualVariantProps } from './visual-variants-types.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT } from '../../constants.js';
import type {
  InitialTableState,
  RowData,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

/**
 * Typeguard function to make sure a passed vertical alignment is an extended config.
 * @internal
 */
function isVerticalAlignmentExtendedConfig(
  verticalAlignment?:
    | 'top'
    | 'center'
    | 'bottom'
    | {
        header: 'top' | 'center' | 'bottom';
        body: 'top' | 'center' | 'bottom';
      },
): verticalAlignment is {
  header: 'top' | 'center' | 'bottom';
  body: 'top' | 'center' | 'bottom';
} {
  if (
    verticalAlignment !== undefined &&
    typeof verticalAlignment !== 'string'
  ) {
    return true;
  }

  return false;
}

export function useVisualVariant<T>(
  props: DataTableVisualVariantProps,
  options: TableOptions<T>,
) {
  const configVariant = useDataTableConfigElement('variant');

  options.state = {
    ...options.state,
    visualVariantConfig: {
      rowSeparation:
        props.variant?.rowSeparation ??
        configVariant?.rowSeparation ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowSeparation,
      verticalDividers:
        props.variant?.verticalDividers ??
        configVariant?.verticalDividers ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalDividers,
      contained:
        props.variant?.contained ??
        configVariant?.contained ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.contained,
      rowDensity:
        props.variant?.rowDensity ??
        configVariant?.rowDensity ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowDensity,
      fontStyle:
        props.variant?.fontStyle ??
        configVariant?.fontStyle ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.fontStyle,
      headers:
        props.variant?.headers ??
        configVariant?.headers ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.headers,
      verticalAlignment:
        props.variant?.verticalAlignment ??
        configVariant?.verticalAlignment ??
        DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalAlignment,
    },
  };
}

export const VisualVariantConfig: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      visualVariantConfig: {
        rowSeparation: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowSeparation,
        verticalDividers:
          DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalDividers,
        contained: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.contained,
        rowDensity: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.rowDensity,
        fontStyle: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.fontStyle,
        headers: DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.headers,
        verticalAlignment:
          DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT.verticalAlignment,
      },
      ...state,
    };
  },

  createTable<TData extends RowData>(table: Table<TData>): void {
    table.getVisualVariantOptions = () => {
      return table.getState().visualVariantConfig;
    };

    table.getBodyVerticalAlignment = memo(
      () => [table.getVisualVariantOptions().verticalAlignment],
      (verticalAlignment) => {
        if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
          return verticalAlignment['body'];
        }

        return verticalAlignment;
      },
      { key: isDev() && 'getBodyVerticalAlignment' },
    );

    table.getHeaderVerticalAlignment = memo(
      () => [table.getVisualVariantOptions().verticalAlignment],
      (verticalAlignment) => {
        if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
          return verticalAlignment['header'];
        }

        return verticalAlignment;
      },
      { key: isDev() && 'getHeaderVerticalAlignment' },
    );
  },
};
